import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { fromPairs, omit, pick, union, without } from "ramda"
import type { TransactionsReportItem } from "./transactions/types"
import type { Customer, CustomerOrder, CustomersFeedbackReportItem } from "../../data/types"
import { TimesheetRecord } from "../../data/types"
import {
    FETCH_BREAKDOWN_BY_HOUR_DATA_SUCCESS,
    FETCH_CASH_REPORT_SUCCESS,
    FETCH_CUSTOMER_ORDERS_REQUESTED,
    FETCH_CUSTOMER_ORDERS_SUCCESS,
    FETCH_CUSTOMERS_FEEDBACK_REPORT_SUCCESS,
    FETCH_CUSTOMERS_REPORT_SUCCESS,
    FETCH_NET_COLLECTED_BREAKDOWN_BY_PAYMENT_TYPE_DATA_SUCCESS,
    FETCH_NET_COLLECTED_BREAKDOWN_DATA_SUCCESS,
    FETCH_NET_COLLECTED_BY_EMPLOYEE_DATA_SUCCESS,
    FETCH_NET_COLLECTED_BY_EMPLOYEE_DETAIL_DATA_SUCCESS,
    FETCH_NET_COLLECTED_BY_SOURCE_DATA_SUCCESS,
    FETCH_NET_REVENUE_BY_ASSIGNMENT_DATA_SUCCESS,
    FETCH_NET_REVENUE_BY_CATEGORY_DATA_SUCCESS,
    FETCH_ORDER_DETAIL_REQUESTED,
    FETCH_ORDER_DETAIL_SUCCESS,
    FETCH_ORDERS_REPORT_SUCCESS,
    FETCH_PREPARATION_TIME_BY_ITEM_REPORT_FAILURE,
    FETCH_PREPARATION_TIME_BY_ITEM_REPORT_SUCCESS,
    FETCH_PREPARATION_TIME_HOURLY_REPORT_FAILURE,
    FETCH_PREPARATION_TIME_HOURLY_REPORT_SUCCESS,
    FETCH_TIPS_AND_GRATUITY_BY_ASSIGNMENT_DATA_SUCCESS,
    FETCH_TRANSACTIONS_BY_PAYMENT_TYPE_DATA_SUCCESS,
    FETCH_TRENDING_DATA_SUCCESS,
} from "../../../api/actions"
import { CUSTOMER_DELETED, CUSTOMER_UPDATED, TIME_RECORD_DELETED, TIME_RECORD_UPDATED } from "../../data/actions"
import { CLEAR_CUSTOMERS_DATA, CLEAR_CUSTOMERS_FEEDBACK_DATA } from "./customers/actions"
import {
    CLEAR_ORDERS_DATA,
    GET_PAYMENT_REQUEST_DETAIL_SUCCESS,
    SEND_ORDER_EMAIL_CLICKED,
    SEND_ORDER_EMAIL_FAILURE,
    SEND_ORDER_EMAIL_RESET_DEFAULT,
    SEND_ORDER_EMAIL_SUCCESS,
} from "./orders/actions"
import { CLEAR_CASH_REPORT_DATA } from "./cash/actions"
import type {
    BreakdownByHourItem,
    NetCollectedBreakdownDataType,
    NetCollectedByEmployeeDataType,
    NetCollectedByEmployeeDetailDataType,
    NetCollectedBySourceDataType,
    NetRevenueByAssignmentDataType,
    NetRevenueByCategoryDataType,
    TransactionsByPaymentTypeDataType,
    TrendingDataItem,
} from "./summary/types"
import { NetCollectedByPaymentTypeDataType } from "./summary/types"
import { CLEAR_SALES_BY_ITEMS_REPORT_DATA } from "./salesByItems/actions"
import {
    ADD_SHIFT_MODAL_OPENED,
    CLEAR_TIMESHEET_DATA,
    CLOCK_IN_OUT_POPOVER_CONFIRM_CLICKED,
    EDIT_SHIFT_MODAL_OPENED,
    FETCH_TIMESHEET_REPORT_SUCCESS,
} from "./timesheet/actions"
import { CLEAR_SUMMARY_DATA, SUMMARY_SELECT_ASSIGNEES_CLICKED } from "./summary/actions"
import { CLEAR_TRANSACTIONS_DATA, FETCH_TRANSACTIONS_REPORT_SUCCESS } from "./transactions/actions"
import type { OrderReportItem } from "./orders/types"
import type {
    CashReportItem,
    CustomerReportItem,
    OrderDetail,
    PreparationTimeByItemReport,
    PreparationTimeHourlyReport,
    SpecialPricingLogReportItem,
    SpecialPricingSummaryReportItem,
    VoidsLogReportItem,
    VoidsSummaryReportItem,
} from "../../../graphql/types"
import { TipsAndGratuityByAssignmentReportItem } from "../../../graphql/types"
import {
    CLEAR_SPECIAL_PRICING_REPORT_DATA,
    FETCH_SPECIAL_PRICINGS_EMPLOYEE_REPORT_SUCCESS,
    FETCH_SPECIAL_PRICINGS_LOG_REPORT_SUCCESS,
    FETCH_SPECIAL_PRICINGS_SUMMARY_REPORT_SUCCESS,
} from "./specialPricing/actions"
import {
    CLEAR_VOIDS_REPORT_DATA,
    FETCH_VOIDS_LOG_REPORT_SUCCESS,
    FETCH_VOIDS_SUMMARY_REPORT_SUCCESS,
} from "./voids/actions"
import {
    GOOGLE_REVIEWS_FETCH_REQUESTED,
    GOOGLE_REVIEWS_FETCH_SUCCEEDED,
    GOOGLE_REVIEWS_FETCH_FAILED,
    GOOGLE_REVIEWS_PAGE_SELECTED,
    GOOGLE_REVIEW_RESPONSE_PANEL_OPENED,
    GOOGLE_REVIEW_RESPONSE_PANEL_CLOSED,
    GOOGLE_REVIEW_RESPONSE_SUBMIT_REQUESTED,
    GOOGLE_REVIEW_RESPONSE_SUBMIT_SUCCEEDED,
    GOOGLE_REVIEW_RESPONSE_SUBMIT_FAILED,
    GOOGLE_REVIEW_RESPONSE_DELETE_REQUESTED,
    GOOGLE_REVIEW_RESPONSE_DELETE_SUCCEEDED,
    GOOGLE_REVIEW_RESPONSE_DELETE_FAILED,
    GOOGLE_REVIEW_REPLY_SUGGESTIONS_FETCH_REQUESTED,
    GOOGLE_REVIEW_REPLY_SUGGESTIONS_FETCH_SUCCEEDED,
    GOOGLE_REVIEW_REPLY_SUGGESTIONS_FETCH_FAILED,
    GOOGLE_REVIEW_REPLY_SUGGESTION_DISMISSED,
    GOOGLE_REVIEW_DRAFT_CHANGED,
} from "./customers/googleReviews/actions"
import { LOCATION_INITIALIZED, LOCATION_SELECTED } from "../../common/locationsPicker/actions"
import { hasReply } from "./customers/googleReviews/utils"
import type {
    GoogleReview,
    GoogleReviewReplyInput,
    GoogleReviewReplySuggestionResult,
} from "./customers/googleReviews/types"
import type { Dictionary } from "../../../utils/types"
// Customers

export type CustomerPagesObject = Record<number, CustomerReportItem[]>

export type CustomersState = {
    customerOrders: CustomerOrder[]
    pages: CustomerPagesObject
}
// Customers Feedback

export type CustomerFeedbackPagesObject = Record<number, CustomersFeedbackReportItem[]>

export type CustomersFeedbackState = {
    pages: CustomerFeedbackPagesObject
}
// Cash Report

export type CashReportPagesObject = Record<number, CashReportItem[]>

export type CashReportState = {
    pages: CashReportPagesObject
}
// Preparation Time Report

export type PreparationTimeReportState = {
    byItem: PreparationTimeByItemReport
    hourly: PreparationTimeHourlyReport
}
// Orders

export type OrderPagesObject = Record<number, OrderReportItem[]>

export const OrderEmailSendingStateEnum = {
    SUCCESS: "success",
    FAILURE: "failure",
    PROCESSING: "processing",
}

export type OrderEmailSendingState = (typeof OrderEmailSendingStateEnum)[keyof typeof OrderEmailSendingStateEnum]

export type OrdersState = {
    pages: OrderPagesObject
    orderDetail: Maybe<OrderDetail>
    emailSendingState: Maybe<OrderEmailSendingState>
    paymentRequestsDetail: Dictionary
}
// Timesheet

export type TimesheetState = {
    loadingUserAccountIds: string[]
    addShiftModalUserAccountId: Maybe<string>
    editShiftModalData: Maybe<TimesheetRecord>
}

// Google Reviews

// Reply drafts and AI suggestions, all keyed by reviewId so that one row's activity
// never blocks another's. Reset whenever the selected location changes.
export type GoogleReviewRepliesState = {
    // Data held per review, keyed by reviewId.
    drafts: Record<string, string>
    suggestions: Record<string, GoogleReviewReplySuggestionResult>
    // Reviews whose last generation attempt failed, by message.
    suggestionErrors: Record<string, string>

    // Which reviews currently have a request in flight.
    generatingReviewIds: string[]
    submittingReviewIds: string[]
}

export type GoogleReviewsState = GoogleReviewRepliesState & {
    // Reviews data
    reviews: GoogleReview[]
    reviewsLoading: boolean
    reviewsError: Maybe<string>
    currentPage: number
    totalReviewCount: number
    lastLocationId: Maybe<string>
    selectedReview: Maybe<GoogleReview>
    responseError: Maybe<string>
}

export const createEmptyGoogleReviewRepliesState = (): GoogleReviewRepliesState => ({
    drafts: {},
    suggestions: {},
    suggestionErrors: {},
    generatingReviewIds: [],
    submittingReviewIds: [],
})

const isReviewOpenInDrawer = (state: GoogleReviewsState, reviewId: string) =>
    state.selectedReview?.reviewId === reviewId

const forgetReview = (state: GoogleReviewRepliesState, reviewId: string): GoogleReviewRepliesState => ({
    drafts: omit([reviewId], state.drafts),
    suggestions: omit([reviewId], state.suggestions),
    suggestionErrors: omit([reviewId], state.suggestionErrors),
    generatingReviewIds: without([reviewId], state.generatingReviewIds),
    submittingReviewIds: without([reviewId], state.submittingReviewIds),
})

// Reports

export type ReportsState = {
    cash: CashReportState
    customers: CustomersState
    customersFeedback: CustomersFeedbackState
    orders: OrdersState
    transactions: TransactionsReportItem[]
    summary: SummaryState
    specialPricingLogs: Maybe<SpecialPricingLogReportItem[]>
    specialPricingSummary: Maybe<SpecialPricingSummaryReportItem[]>
    specialPricingByEmployee: Maybe<SpecialPricingSummaryReportItem[]>
    voidsLogs: Maybe<VoidsLogReportItem[]>
    voidsSummary: Maybe<VoidsSummaryReportItem[]>
    timesheet: TimesheetState
    preparationTime: PreparationTimeReportState
    googleReviews: GoogleReviewsState
}
// Summary

export type SummaryState = {
    netCollectedBreakdownData: NetCollectedBreakdownDataType
    netCollectedByEmployeeData: NetCollectedByEmployeeDataType[]
    netCollectedByEmployeeDetailData: NetCollectedByEmployeeDetailDataType[]
    netCollectedByPaymentType: NetCollectedByPaymentTypeDataType
    netRevenueByAssignmentData: NetRevenueByAssignmentDataType[]
    tipsAndGratuityByAssignmentData: TipsAndGratuityByAssignmentReportItem[]
    netRevenueByCategoryData: NetRevenueByCategoryDataType[]
    transactionsByPaymentTypeData: TransactionsByPaymentTypeDataType[]
    netCollectedBySourceData: NetCollectedBySourceDataType[]
    trending: Maybe<TrendingDataItem[]>
    breakdownByHour: Maybe<BreakdownByHourItem[]>
    selectedAssigneeIds: Maybe<string[]>
}
const summaryDefaultState = {
    summary: {
        netCollectedBreakdownData: {
            grossRevenue: 0,
            refundsTotal: 0,
            voidsTotal: 0,
            specialPricings: [],
            netRevenue: 0,
            taxes: [],
            tipsTotal: 0,
            historicalRefunds: 0,
            futureRefunds: 0,
            netCollected: 0,
            losses: 0,
            forfeits: 0,
            tipsAndGratuityTotal: 0,
            gratuityTotal: 0,
            cardSurcharge: 0,
            cashDiscount: 0,
            serviceFee: 0,
            tips: [],
        },
        netCollectedByPaymentType: {
            paymentType: null,
            grossRevenue: 0,
            refundsTotal: 0,
            cardSurcharge: 0,
            cashDiscount: 0,
            serviceFee: 0,
            voidsTotal: 0,
            netRevenue: 0,
            tipsTotal: 0,
            historicalRefunds: 0,
            futureRefunds: 0,
            netCollected: 0,
            losses: 0,
            forfeits: 0,
            gratuityTotal: 0,
            tipsAndGratuityTotal: 0,
            taxes: [],
            specialPricings: [],
            tips: [],
            children: [],
        },
        netCollectedByEmployeeData: [
            {
                id: "Root",
                firstName: null,
                surname: null,
                cashTotal: 0,
                cardTotal: 0,
                externalTotal: 0,
                total: 0,
                redemptionTotal: 0,
                children: [],
            },
        ],
        netCollectedByEmployeeDetailData: [
            {
                id: "Root",
                firstName: null,
                surname: null,
                grossRevenueTotal: 0,
                voidsTotal: 0,
                refundsTotal: 0,
                specialPricingsTotal: 0,
                lossesTotal: 0,
                forfeitedTotal: 0,
                taxTotal: 0,
                tipsTotal: 0,
                total: 0,
                children: [],
            },
        ],
        netRevenueByAssignmentData: [
            {
                id: "Root",
                name: "Root",
                quantity: 0,
                quantityPercentage: 0,
                revenue: 0,
                revenuePercentage: 0,
                children: [],
            },
        ],
        tipsAndGratuityByAssignmentData: [],
        netCollectedBySourceData: [
            {
                id: "Root",
                name: "Root",
                count: 0,
                countPercentage: 0,
                total: 0,
                totalPercentage: 0,
                children: [],
            },
        ],
        netRevenueByCategoryData: [
            {
                id: "Root",
                name: "Root",
                quantity: 0,
                quantityPercentage: 0,
                revenue: 0,
                revenuePercentage: 0,
                unit: null,
                unitQuantity: null,
                children: [],
            },
        ],
        transactionsByPaymentTypeData: [
            {
                name: "Root",
                creditsCount: 0,
                debitsCount: 0,
                netCount: 0,
                percentage: 0,
                total: 0,
                children: [
                    {
                        name: "Cash",
                        creditsCount: 0,
                        debitsCount: 0,
                        netCount: 0,
                        percentage: 0,
                        total: 0,
                        children: [],
                    },
                    {
                        name: "Credit Card",
                        creditsCount: 0,
                        debitsCount: 0,
                        netCount: 0,
                        percentage: 0,
                        total: 0,
                        children: [],
                    },
                    {
                        name: "External",
                        creditsCount: 0,
                        debitsCount: 0,
                        netCount: 0,
                        percentage: 0,
                        total: 0,
                        children: [],
                    },
                ],
            },
        ],
        trending: [],
        breakdownByHour: [],
        selectedAssigneeIds: null,
    },
}

export const createDefaultReportsState = (): ReportsState => ({
    cash: {
        pages: {
            1: [],
        },
    },
    orders: {
        pages: {
            1: [],
        },
        orderDetail: null,
        emailSendingState: null,
        paymentRequestsDetail: {},
    },
    customers: {
        customerOrders: [],
        pages: {
            1: [],
        },
    },
    customersFeedback: {
        pages: {
            1: [],
        },
    },
    transactions: [],
    specialPricingLogs: null,
    specialPricingByEmployee: null,
    specialPricingSummary: null,
    voidsLogs: null,
    voidsSummary: null,
    timesheet: {
        loadingUserAccountIds: [],
        addShiftModalUserAccountId: null,
        editShiftModalData: null,
    },
    preparationTime: {
        byItem: {
            items: [],
        },
        hourly: {
            hours: [],
        },
    },
    googleReviews: {
        reviews: [],
        reviewsLoading: false,
        reviewsError: null,
        currentPage: 1,
        totalReviewCount: 0,
        lastLocationId: null,
        selectedReview: null,
        responseError: null,
        ...createEmptyGoogleReviewRepliesState(),
    },
    ...summaryDefaultState,
})
export const reportsReducer = (state: Maybe<ReportsState> = undefined, action: Record<string, any>) => {
    if (!state) {
        return createDefaultReportsState()
    }

    switch (action.type) {
        // Special Pricing Logs
        case FETCH_SPECIAL_PRICINGS_LOG_REPORT_SUCCESS:
            return { ...state, specialPricingLogs: action.payload.data }

        // Special Pricing Summary
        case FETCH_SPECIAL_PRICINGS_SUMMARY_REPORT_SUCCESS:
            return { ...state, specialPricingSummary: action.payload.data }

        // Special Pricing By Employee
        case FETCH_SPECIAL_PRICINGS_EMPLOYEE_REPORT_SUCCESS:
            return { ...state, specialPricingByEmployee: action.payload.data }

        // Voids Logs
        case FETCH_VOIDS_LOG_REPORT_SUCCESS:
            return { ...state, voidsLogs: action.payload.data }

        // Voids Summary
        case FETCH_VOIDS_SUMMARY_REPORT_SUCCESS:
            return { ...state, voidsSummary: action.payload.data }

        // Transactions
        case FETCH_TRANSACTIONS_REPORT_SUCCESS:
            return { ...state, transactions: action.payload.transactionsReport }

        case CLEAR_TRANSACTIONS_DATA:
            return { ...state, transactions: [] }

        // Customers
        case FETCH_CUSTOMERS_REPORT_SUCCESS:
            return {
                ...state,
                customers: {
                    ...state.customers,
                    pages: { ...state.customers.pages, [action.payload.currentPage]: action.payload.data },
                },
            }

        case CLEAR_CUSTOMERS_DATA:
            return {
                ...state,
                customers: {
                    ...state.customers,
                    pages: {
                        1: [],
                    },
                },
            }

        case FETCH_CUSTOMER_ORDERS_REQUESTED:
            return { ...state, customers: { ...state.customers, customerOrders: [] } }

        case FETCH_CUSTOMER_ORDERS_SUCCESS:
            return { ...state, customers: { ...state.customers, customerOrders: action.payload.data } }

        case CUSTOMER_UPDATED: {
            return {
                ...state,
                customers: {
                    ...state.customers,
                    pages: {
                        ...state.customers.pages,
                        [action.payload.currentPage]: state.customers.pages[action.payload.currentPage].map(c => {
                            const updatedCustomer: Customer = action.payload.newValue

                            if (c.id !== updatedCustomer.id) {
                                return c
                            } else {
                                return {
                                    ...c,
                                    companyName: updatedCustomer.companyName,
                                    customerPersonalName: `${updatedCustomer.firstName || ""} ${
                                        updatedCustomer.surname || ""
                                    }`,
                                    isCompany: updatedCustomer.isCompany,
                                    email: updatedCustomer.email,
                                    phoneNumber: updatedCustomer.phoneNumber,
                                }
                            }
                        }),
                    },
                },
            }
        }

        case CUSTOMER_DELETED: {
            return {
                ...state,
                customers: {
                    ...state.customers,
                    pages: {
                        ...state.customers.pages,
                        [action.payload.currentPage]: state.customers.pages[action.payload.currentPage].filter(
                            c => c.id !== action.payload.customer.id
                        ),
                    },
                },
            }
        }

        // Customers feedback
        case FETCH_CUSTOMERS_FEEDBACK_REPORT_SUCCESS:
            return {
                ...state,
                customersFeedback: {
                    ...state.customersFeedback,
                    pages: { ...state.customersFeedback.pages, [action.payload.currentPage]: action.payload.data },
                },
            }

        case CLEAR_CUSTOMERS_FEEDBACK_DATA:
            return {
                ...state,
                customersFeedback: {
                    ...state.customersFeedback,
                    pages: {
                        1: [],
                    },
                },
            }

        // Orders
        case FETCH_ORDERS_REPORT_SUCCESS:
            return {
                ...state,
                orders: {
                    ...state.orders,
                    totalCount: action.payload.totalCount,
                    currentPage: action.payload.currentPage,
                    pages: { ...state.orders.pages, [action.payload.currentPage]: action.payload.data },
                },
            }

        case CLEAR_ORDERS_DATA:
            return {
                ...state,
                orders: {
                    ...state.orders,
                    pages: {
                        1: [],
                    },
                    orderDetail: null,
                },
            }

        case SEND_ORDER_EMAIL_CLICKED:
            return { ...state, orders: { ...state.orders, emailSendingState: OrderEmailSendingStateEnum.PROCESSING } }

        case SEND_ORDER_EMAIL_SUCCESS:
            return { ...state, orders: { ...state.orders, emailSendingState: OrderEmailSendingStateEnum.SUCCESS } }

        case SEND_ORDER_EMAIL_FAILURE:
            return { ...state, orders: { ...state.orders, emailSendingState: OrderEmailSendingStateEnum.FAILURE } }

        case SEND_ORDER_EMAIL_RESET_DEFAULT:
            return { ...state, orders: { ...state.orders, emailSendingState: null } }

        case FETCH_ORDER_DETAIL_REQUESTED:
            return { ...state, orders: { ...state.orders, orderDetail: null } }

        case FETCH_ORDER_DETAIL_SUCCESS:
            return { ...state, orders: { ...state.orders, orderDetail: action.payload.orderDetail } }

        case GET_PAYMENT_REQUEST_DETAIL_SUCCESS:
            return {
                ...state,
                orders: {
                    ...state.orders,
                    paymentRequestsDetail: { ...state.orders.paymentRequestsDetail, ...action.payload.data },
                },
            }

        // Preparation Time
        case FETCH_PREPARATION_TIME_BY_ITEM_REPORT_SUCCESS:
            return { ...state, preparationTime: { ...state.preparationTime, byItem: action.payload.data } }

        case FETCH_PREPARATION_TIME_BY_ITEM_REPORT_FAILURE:
            return {
                ...state,
                preparationTime: {
                    ...state.preparationTime,
                    byItem: {
                        items: [],
                    },
                },
            }

        case FETCH_PREPARATION_TIME_HOURLY_REPORT_SUCCESS:
            return { ...state, preparationTime: { ...state.preparationTime, hourly: action.payload.data } }

        case FETCH_PREPARATION_TIME_HOURLY_REPORT_FAILURE:
            return {
                ...state,
                preparationTime: {
                    ...state.preparationTime,
                    hourly: {
                        hours: [],
                    },
                },
            }

        // Summary
        case CLEAR_SUMMARY_DATA:
            return { ...state, ...summaryDefaultState }

        case FETCH_NET_COLLECTED_BREAKDOWN_DATA_SUCCESS:
            return { ...state, summary: { ...state.summary, netCollectedBreakdownData: action.payload.data } }

        case FETCH_NET_COLLECTED_BY_EMPLOYEE_DATA_SUCCESS:
            return { ...state, summary: { ...state.summary, netCollectedByEmployeeData: action.payload.data } }

        case FETCH_NET_COLLECTED_BREAKDOWN_BY_PAYMENT_TYPE_DATA_SUCCESS:
            return { ...state, summary: { ...state.summary, netCollectedByPaymentType: action.payload.data } }

        case FETCH_TIPS_AND_GRATUITY_BY_ASSIGNMENT_DATA_SUCCESS:
            return { ...state, summary: { ...state.summary, tipsAndGratuityByAssignmentData: action.payload.data } }

        case FETCH_NET_COLLECTED_BY_EMPLOYEE_DETAIL_DATA_SUCCESS:
            return { ...state, summary: { ...state.summary, netCollectedByEmployeeDetailData: action.payload.data } }

        case FETCH_NET_REVENUE_BY_ASSIGNMENT_DATA_SUCCESS:
            return { ...state, summary: { ...state.summary, netRevenueByAssignmentData: action.payload.data } }

        case FETCH_NET_COLLECTED_BY_SOURCE_DATA_SUCCESS:
            return { ...state, summary: { ...state.summary, netCollectedBySourceData: action.payload.data } }

        case FETCH_NET_REVENUE_BY_CATEGORY_DATA_SUCCESS:
            return { ...state, summary: { ...state.summary, netRevenueByCategoryData: action.payload.data } }

        case FETCH_TRANSACTIONS_BY_PAYMENT_TYPE_DATA_SUCCESS:
            return { ...state, summary: { ...state.summary, transactionsByPaymentTypeData: action.payload.data } }

        // Cash Report
        case FETCH_CASH_REPORT_SUCCESS:
            return {
                ...state,
                cash: {
                    ...state.cash,
                    totalCount: action.payload.totalCount,
                    currentPage: action.payload.currentPage,
                    pages: { ...state.cash.pages, [action.payload.currentPage]: action.payload.data },
                },
            }

        case CLEAR_CASH_REPORT_DATA:
            return {
                ...state,
                cash: {
                    ...state.cash,
                    pages: {
                        1: [],
                    },
                },
            }

        case CLEAR_SALES_BY_ITEMS_REPORT_DATA:
            return { ...state, salesByModifiers: [], salesByModifierGroups: [] }

        case CLEAR_SPECIAL_PRICING_REPORT_DATA:
            return { ...state, specialPricingLogs: null, specialPricingSummary: null, specialPricingByEmployee: null }

        case CLEAR_VOIDS_REPORT_DATA:
            return { ...state, voidsLogs: null, voidsSummary: null }

        case FETCH_TIMESHEET_REPORT_SUCCESS: {
            return {
                ...state,
                timesheet: {
                    ...state.timesheet,
                    loadingUserAccountIds: [],
                },
            }
        }

        case CLEAR_TIMESHEET_DATA: {
            return {
                ...state,
                timesheet: {
                    loadingUserAccountIds: [],
                    addShiftModalUserAccountId: null,
                    editShiftModalData: null,
                },
            }
        }

        case ADD_SHIFT_MODAL_OPENED:
            return {
                ...state,
                timesheet: { ...state.timesheet, addShiftModalUserAccountId: action.payload.userAccountId },
            }

        case EDIT_SHIFT_MODAL_OPENED:
            return {
                ...state,
                timesheet: { ...state.timesheet, editShiftModalData: action.payload.shiftDetail },
            }

        case CLOCK_IN_OUT_POPOVER_CONFIRM_CLICKED:
            return {
                ...state,
                timesheet: {
                    ...state.timesheet,
                    loadingUserAccountIds: [
                        ...state.timesheet.loadingUserAccountIds,
                        action.payload.params.userAccountId,
                    ],
                },
            }

        case TIME_RECORD_DELETED:
            return {
                ...state,
                timesheet: {
                    ...state.timesheet,
                    loadingUserAccountIds: [...state.timesheet.loadingUserAccountIds, action.payload.userAccountId],
                },
            }

        case TIME_RECORD_UPDATED:
            return {
                ...state,
                timesheet: {
                    ...state.timesheet,
                    loadingUserAccountIds: [
                        ...state.timesheet.loadingUserAccountIds,
                        action.payload.newValue.userAccountId,
                    ],
                },
            }

        case FETCH_TRENDING_DATA_SUCCESS: {
            return { ...state, summary: { ...state.summary, trending: action.payload.data, breakdownByHour: null } }
        }

        case FETCH_BREAKDOWN_BY_HOUR_DATA_SUCCESS: {
            return { ...state, summary: { ...state.summary, breakdownByHour: action.payload.data, trending: null } }
        }

        case SUMMARY_SELECT_ASSIGNEES_CLICKED: {
            return { ...state, summary: { ...state.summary, selectedAssigneeIds: action.payload.ids } }
        }

        // Switching location invalidates every draft and suggestion, and is what the
        // suggestions epic cancels on — so both sides agree on one signal.
        case LOCATION_INITIALIZED:
        case LOCATION_SELECTED:
            return {
                ...state,
                googleReviews: {
                    ...state.googleReviews,
                    ...createEmptyGoogleReviewRepliesState(),
                },
            }

        case GOOGLE_REVIEWS_FETCH_REQUESTED: {
            const isLocationChanged = action.payload.locationId !== state.googleReviews.lastLocationId
            return {
                ...state,
                googleReviews: {
                    ...state.googleReviews,
                    reviewsLoading: true,
                    reviewsError: null,
                    currentPage: isLocationChanged ? 1 : state.googleReviews.currentPage,
                    totalReviewCount: isLocationChanged ? 0 : state.googleReviews.totalReviewCount,
                    reviews: isLocationChanged ? [] : state.googleReviews.reviews,
                    lastLocationId: action.payload.locationId,
                },
            }
        }

        case GOOGLE_REVIEWS_FETCH_SUCCEEDED: {
            const reviews: GoogleReview[] = action.payload.reviews ?? []
            // Drop drafts and suggestions for reviews that are gone or answered elsewhere.
            const unansweredIds = reviews.filter(review => !hasReply(review)).map(review => review.reviewId)

            return {
                ...state,
                googleReviews: {
                    ...state.googleReviews,
                    reviewsLoading: false,
                    reviews: reviews,
                    totalReviewCount: action.payload.totalReviewCount ?? state.googleReviews.totalReviewCount,
                    drafts: pick(unansweredIds, state.googleReviews.drafts),
                    suggestions: pick(unansweredIds, state.googleReviews.suggestions),
                    suggestionErrors: pick(unansweredIds, state.googleReviews.suggestionErrors),
                },
            }
        }

        case GOOGLE_REVIEWS_FETCH_FAILED:
            return {
                ...state,
                googleReviews: {
                    ...state.googleReviews,
                    reviewsLoading: false,
                    reviewsError: action.payload.error,
                },
            }

        case GOOGLE_REVIEWS_PAGE_SELECTED:
            return {
                ...state,
                googleReviews: {
                    ...state.googleReviews,
                    currentPage: action.payload.pageNumber,
                },
            }

        // Response panel actions
        case GOOGLE_REVIEW_RESPONSE_PANEL_OPENED:
            return {
                ...state,
                googleReviews: {
                    ...state.googleReviews,
                    selectedReview: action.payload.review,
                    responseError: null,
                },
            }

        case GOOGLE_REVIEW_RESPONSE_PANEL_CLOSED:
            return {
                ...state,
                googleReviews: {
                    ...state.googleReviews,
                    selectedReview: null,
                    responseError: null,
                },
            }

        case GOOGLE_REVIEW_RESPONSE_SUBMIT_REQUESTED:
        case GOOGLE_REVIEW_RESPONSE_DELETE_REQUESTED:
            return {
                ...state,
                googleReviews: {
                    ...state.googleReviews,
                    submittingReviewIds: union(state.googleReviews.submittingReviewIds, [action.payload.reviewId]),
                    responseError: null,
                },
            }

        case GOOGLE_REVIEW_RESPONSE_SUBMIT_SUCCEEDED:
            return {
                ...state,
                googleReviews: {
                    ...state.googleReviews,
                    ...forgetReview(state.googleReviews, action.payload.reviewId),
                    selectedReview: isReviewOpenInDrawer(state.googleReviews, action.payload.reviewId)
                        ? null
                        : state.googleReviews.selectedReview,
                    // Update the review in the list with the new response
                    reviews: state.googleReviews.reviews.map(review =>
                        review.reviewId === action.payload.reviewId
                            ? { ...review, reviewReply: action.payload.reply }
                            : review
                    ),
                },
            }

        case GOOGLE_REVIEW_RESPONSE_SUBMIT_FAILED:
        case GOOGLE_REVIEW_RESPONSE_DELETE_FAILED:
            return {
                ...state,
                googleReviews: {
                    ...state.googleReviews,
                    submittingReviewIds: without([action.payload.reviewId], state.googleReviews.submittingReviewIds),
                    // Only the drawer renders this; an inline failure is reported by its toast.
                    responseError: isReviewOpenInDrawer(state.googleReviews, action.payload.reviewId)
                        ? action.payload.error
                        : state.googleReviews.responseError,
                },
            }

        case GOOGLE_REVIEW_RESPONSE_DELETE_SUCCEEDED:
            return {
                ...state,
                googleReviews: {
                    ...state.googleReviews,
                    ...forgetReview(state.googleReviews, action.payload.reviewId),
                    selectedReview: isReviewOpenInDrawer(state.googleReviews, action.payload.reviewId)
                        ? null
                        : state.googleReviews.selectedReview,
                    // Remove the response from the review
                    reviews: state.googleReviews.reviews.map(review =>
                        review.reviewId === action.payload.reviewId ? { ...review, reviewReply: null } : review
                    ),
                },
            }

        case GOOGLE_REVIEW_REPLY_SUGGESTIONS_FETCH_REQUESTED: {
            const reviewIds = action.payload.reviews.map((review: GoogleReviewReplyInput) => review.reviewId)

            return {
                ...state,
                googleReviews: {
                    ...state.googleReviews,
                    generatingReviewIds: union(state.googleReviews.generatingReviewIds, reviewIds),
                    suggestionErrors: omit(reviewIds, state.googleReviews.suggestionErrors),
                },
            }
        }

        case GOOGLE_REVIEW_REPLY_SUGGESTIONS_FETCH_SUCCEEDED: {
            const results: GoogleReviewReplySuggestionResult[] = action.payload.results
            const suggestions = { ...state.googleReviews.suggestions }
            const suggestionErrors = { ...state.googleReviews.suggestionErrors }
            const drafts = { ...state.googleReviews.drafts }

            results.forEach(result => {
                const suggestion = result.suggestions?.[0]

                if (suggestion) {
                    suggestions[result.reviewId] = result
                    // A fresh suggestion replaces the draft it was asked to improve on.
                    drafts[result.reviewId] = suggestion
                    delete suggestionErrors[result.reviewId]
                } else {
                    suggestionErrors[result.reviewId] = result.error || "Could not generate a response"
                }
            })

            return {
                ...state,
                googleReviews: {
                    ...state.googleReviews,
                    generatingReviewIds: without(
                        results.map(result => result.reviewId),
                        state.googleReviews.generatingReviewIds
                    ),
                    suggestions: suggestions,
                    suggestionErrors: suggestionErrors,
                    drafts: drafts,
                },
            }
        }

        case GOOGLE_REVIEW_REPLY_SUGGESTIONS_FETCH_FAILED: {
            const reviewIds: string[] = action.payload.reviewIds

            return {
                ...state,
                googleReviews: {
                    ...state.googleReviews,
                    generatingReviewIds: without(reviewIds, state.googleReviews.generatingReviewIds),
                    suggestionErrors: {
                        ...state.googleReviews.suggestionErrors,
                        ...fromPairs(reviewIds.map(reviewId => [reviewId, action.payload.error])),
                    },
                },
            }
        }

        case GOOGLE_REVIEW_REPLY_SUGGESTION_DISMISSED:
            return {
                ...state,
                googleReviews: {
                    ...state.googleReviews,
                    ...forgetReview(state.googleReviews, action.payload.reviewId),
                },
            }

        case GOOGLE_REVIEW_DRAFT_CHANGED:
            return {
                ...state,
                googleReviews: {
                    ...state.googleReviews,
                    drafts: {
                        ...state.googleReviews.drafts,
                        [action.payload.reviewId]: action.payload.message,
                    },
                },
            }

        default:
            return state
    }
}
